import { Plus, X } from "lucide-react";
import StringListEditor from "./StringListEditor";
import { QuestionTypeEditorProps } from "./types";

// dropdown: optionsJson { blanks: { blank_1:[opts], ... } },
// correctAnswerJson { blank_1: correct }. Blanks are named positionally
// (blank_1, blank_2 ...) and re-keyed on add/remove. Prompt references them
// with [blank_1] tokens.
const DropdownEditor = ({
  optionsJson,
  correctAnswerJson,
  onChange,
}: QuestionTypeEditorProps) => {
  const blanks = (optionsJson.blanks as Record<string, string[]>) ?? {};
  const answer = correctAnswerJson as Record<string, string>;

  // Stable order by numeric suffix.
  const names = Object.keys(blanks).sort(
    (a, b) => Number(a.split("_")[1]) - Number(b.split("_")[1])
  );

  // Rebuild blank_1..n from an ordered list of [options, correct] pairs.
  const emit = (rows: { options: string[]; correct: string }[]) => {
    const nextBlanks: Record<string, string[]> = {};
    const nextAnswer: Record<string, string> = {};
    rows.forEach((row, i) => {
      const name = `blank_${i + 1}`;
      nextBlanks[name] = row.options;
      if (row.correct && row.options.includes(row.correct)) {
        nextAnswer[name] = row.correct;
      }
    });
    onChange({
      optionsJson: { blanks: nextBlanks },
      correctAnswerJson: nextAnswer,
    });
  };

  const rows = names.map((n) => ({
    options: blanks[n] ?? [],
    correct: answer[n] ?? "",
  }));

  const setOptions = (i: number, options: string[]) =>
    emit(rows.map((r, idx) => (idx === i ? { ...r, options } : r)));
  const setCorrect = (i: number, correct: string) =>
    emit(rows.map((r, idx) => (idx === i ? { ...r, correct } : r)));
  const addBlank = () => emit([...rows, { options: [], correct: "" }]);
  const removeBlank = (i: number) => emit(rows.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        Reference blanks in the prompt with tokens like{" "}
        <code className="bg-gray-100 px-1 rounded">[blank_1]</code>,{" "}
        <code className="bg-gray-100 px-1 rounded">[blank_2]</code>.
      </p>
      {rows.map((row, i) => (
        <div
          key={i}
          className="p-4 rounded-md border border-black/10 space-y-3"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">
              blank_{i + 1}
            </span>
            <button
              type="button"
              onClick={() => removeBlank(i)}
              className="p-1 text-gray-400 hover:text-red-500"
              title="Remove blank"
            >
              <X size={16} />
            </button>
          </div>
          <StringListEditor
            label="Options"
            values={row.options}
            onChange={(v) => setOptions(i, v)}
            placeholder="Option text"
            addLabel="Add option"
          />
          <div>
            <label className="block font-medium text-burntBlack text-sm mb-1">
              Correct answer
            </label>
            <select
              value={row.correct}
              onChange={(e) => setCorrect(i, e.target.value)}
              className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">— select correct option —</option>
              {row.options.filter(Boolean).map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addBlank}
        className="inline-flex items-center gap-1 text-sm text-[#4379EE] hover:underline"
      >
        <Plus size={14} /> Add blank
      </button>
    </div>
  );
};

export default DropdownEditor;
